import React from 'react';
import { motion } from 'framer-motion';
import { ChefHat, Brain } from 'lucide-react';
import { useMenuGeneration } from '../../../hooks/useMenuGeneration';
import { MenuDisplay } from './MenuDisplay';
import { MenuOptionsPanel } from '../options/MenuOptionsPanel';
import { LoadingIndicator } from '../common/LoadingIndicator';
import { MenuTypeSelector } from './MenuTypeSelector';
import { BabyMenuSection } from '../baby/BabyMenuSection';
import { BabyMenuDisplay } from '../baby/BabyMenuDisplay';

export const MenuContainer: React.FC = () => {
  const {
    currentMenu,
    recipeSteps,
    isLoading,
    isGeneratingRecipe,
    useAI,
    isBabyMenu,
    selectedBabyAge,
    babyMenuOptions,
    menuOptions,
    handleGenerateMenu,
    setUseAI,
    setIsBabyMenu,
    setSelectedBabyAge,
    updateBabyMenuOption,
    toggleOption,
    updateOption,
  } = useMenuGeneration();

  return (
    <div className="space-y-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-lg p-6"
      >
        <div className="flex flex-col md:flex-row justify-between items-center gap-4 mb-8">
          <div className="flex items-center gap-3">
            <ChefHat className="text-orange-500" size={32} />
            <div>
              <h2 className="text-2xl font-bold text-gray-800">Generador de Menús</h2>
              <p className="text-gray-600">Crea menús personalizados con productos de Mercadona</p>
            </div>
          </div>
          <MenuTypeSelector
            useAI={useAI}
            isBabyMenu={isBabyMenu}
            onToggleAI={setUseAI}
            onToggleBabyMenu={setIsBabyMenu}
          />
        </div>

        {isBabyMenu ? (
          <BabyMenuSection
            selectedAge={selectedBabyAge}
            maxPrice={babyMenuOptions.maxPrice}
            courseCount={babyMenuOptions.courseCount}
            onAgeSelect={setSelectedBabyAge}
            onUpdateMaxPrice={(price) => updateBabyMenuOption('maxPrice', price)}
            onUpdateCourseCount={(count) => updateBabyMenuOption('courseCount', count)}
          />
        ) : (
          <div className="bg-white rounded-lg p-6 shadow-sm mb-6">
            <div className="flex items-center gap-2 mb-4">
              <Brain className="text-orange-500" size={24} />
              <h3 className="text-xl font-semibold">Personaliza tu experiencia</h3>
            </div>
            <MenuOptionsPanel
              options={menuOptions}
              onToggleOption={toggleOption}
              onUpdateOption={updateOption}
              useAI={useAI}
            />
          </div>
        )}

        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={handleGenerateMenu}
          className="w-full bg-orange-500 text-white py-4 px-6 rounded-lg hover:bg-orange-600 transition-all flex items-center justify-center gap-3 text-lg font-semibold shadow-lg"
          disabled={isLoading}
        >
          <ChefHat size={24} />
          {isLoading ? 'Generando tu menú...' : 'Generar nuevo menú'}
        </motion.button>

        {(isLoading || isGeneratingRecipe) ? (
          <LoadingIndicator 
            message={isGeneratingRecipe ? "Preparando tu receta..." : "Creando un menú delicioso..."} 
          />
        ) : currentMenu && (
          isBabyMenu ? (
            <BabyMenuDisplay menu={currentMenu} />
          ) : (
            <MenuDisplay
              menu={currentMenu}
              recipeSteps={recipeSteps}
              showRecipe={menuOptions.showPreparation && useAI && !isBabyMenu}
              useThermomix={menuOptions.useThermomix}
            />
          )
        )}
      </motion.div>
    </div>
  );
};